import { useState, useMemo } from "react"
import { PopOver, Icon, Flex, Text, DateFormat, Divider, notification } from "components/common"
import dayjs from "dayjs"
import { Separator } from "components/custom"
import { t } from "i18next"
import theme from "utils/themeVars"
import { useLazyGetProjectsQuery } from "features/shared/api"
import { useLazyGetCompanyOfficesQuery } from "features/tasks/api"
import { getDuration } from "utils/date"
import { localisedValue } from "utils"
import { CUSTOM_TIME_FORMAT } from "constants"

const TASK_STATUS_CONFIG = {
  verified: {
    icon: "FaRegCheckCircle",
    color: "#21aa45",
    locationTitle: "Location Matched",
    locationDesc: "Tracked location matches with the reported meeting location.",
    imageTitle: "Image Verified",
    imageDesc: "Attached image confirms that the meeting took place.",
  },
  unverified: {
    icon: "RxCrossCircled",
    color: "#e83b3b",
    locationTitle: "Location Mismatch",
    locationDesc: "Tracked location does not match with the reported meeting location.",
    imageTitle: "Image Not Verified",
    imageDesc: "Attached image does not meet meeting verification criteria.",
  },
  pending: { icon: "FaRegClock", color: "#d6a700" },
}

const MeetingTaskStatus = ({ data }) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [taskLocation, setTaskLocation] = useState("")

  const verificationStatus = data?.verificationStatus ?? (data?.verified ? "verified" : "unverified")
  const currentStatus = TASK_STATUS_CONFIG[verificationStatus]
  const [getProjects, { isLoading: isLoadingProjects }] = useLazyGetProjectsQuery()
  const [getCompanyOffices, { isLoading: isLoadingOffices }] = useLazyGetCompanyOfficesQuery()

  const fetchTaskLocation = () => {
    if (data?.salesEventId) {
      setTaskLocation(t("Sales Event"))
    } else if (data?.taskProjectId) {
      getProjects(null, true)
        .unwrap()
        .then(list => {
          const currentProject = list?.find(p => p?.id === data?.taskProjectId)
          const projectName = localisedValue({ object: currentProject, key: "name" })
          setTaskLocation(projectName)
        })
        .catch(err => notification.error({ message: err?.message }))
    } else if (data?.agencyOfficeId) {
      getCompanyOffices(null, true)
        .unwrap()
        .then(list => {
          const currentOffice = list?.find(o => o?.id === data?.agencyOfficeId)
          const officeName = localisedValue({ object: currentOffice, key: "title" })
          setTaskLocation(officeName)
        })
        .catch(err => notification.error({ message: err?.message }))
    } else {
      setTaskLocation(data?.localityPlusCode)
    }
  }

  const handlePopoverToggle = visible => {
    setIsPopoverOpen(visible)
    if (visible) fetchTaskLocation()
  }

  const handleLocationClick = () => {
    if (data?.latitude && data?.longitude) {
      window.open(`https://www.google.com/maps?q=${data?.latitude},${data?.longitude}`, "_blank", "noopener,noreferrer")
    }
  }

  const renderStatusSection = (title, desc) => (
    <Flex gap="6px" wrap={false} align="baseline">
      <Icon icon={currentStatus?.icon} color={currentStatus?.color} size={13} style={{ marginTop: "7px" }} />
      <div>
        <Text>{t(title)}</Text>
        <Text className="gray-800 fs14">{t(desc)}</Text>
      </div>
    </Flex>
  )

  const popoverContent = useMemo(() => {
    const meetingStartTime = dayjs(data?.startTime)
    const meetingEndTime = dayjs(data?.endTime)
    const duration = getDuration(meetingStartTime, meetingEndTime)

    return data?.verificationStatus === "pending" ? (
      <div style={{ maxWidth: 430, wordBreak: "break-word" }}>
        <Flex gap="4px" align="center">
          <Icon icon={currentStatus?.icon} color={theme["gray900"]} size={13} />
          <Text className="gray-900 fs14">{t("Location Under Review")}</Text>
        </Flex>
        <Flex gap="6px" align="center">
          <Icon icon={currentStatus?.icon} color={theme["gray900"]} size={13} />
          <Text className="gray-900 fs14">{t("Image Under Review")}</Text>
        </Flex>
      </div>
    ) : (
      <div style={{ maxWidth: 430, wordBreak: "break-word" }}>
        {renderStatusSection(currentStatus?.locationTitle, currentStatus?.locationDesc)}
        {renderStatusSection(currentStatus?.imageTitle, currentStatus?.imageDesc)}
        <Divider style={{ marginBlock: "10px" }} />

        <Flex gap={6} align="center" className="mbe-4">
          <Icon icon="SlLocationPin" size={13} style={{ marginRight: 5 }} color={theme["gray800"]} />
          <Text className="gray-900 fs14" style={{ marginRight: 5, flexShrink: 0 }}>
            {localisedValue({ object: data, key: "name" })}
            {taskLocation ? ":" : ""}
          </Text>
          <Flex
            align="center"
            inline
            style={{
              cursor: data?.latitude && data?.longitude ? "pointer" : "default",
              color: theme["accent-color"],
              whiteSpace: "normal",
              overflowWrap: "break-word",
              maxWidth: "100%",
            }}
            onClick={handleLocationClick}
          >
            {taskLocation}
            {data?.latitude && data?.longitude && (
              <Icon icon="FiExternalLink" size={13} color={theme["accent-color"]} style={{ marginLeft: 5 }} />
            )}
          </Flex>
        </Flex>
        {data?.startTime && data?.endTime && (
          <>
            <Flex align="center" gap={8} style={{ marginTop: 6, flexWrap: "wrap" }}>
              <DateFormat
                date={meetingStartTime}
                showTimeWithTooltip={false}
                dateClassName="gray-900"
                iconColor={theme["gray800"]}
              />
              <Separator />
              <Icon icon="FaRegClock" size={12} color={theme["gray800"]} style={{ marginRight: 4 }} />
              <Text className="gray-900 semiBold fs-14" style={{ marginRight: 5, marginLeft: 5 }}>
                {duration}
              </Text>
              <Text className="gray-900 fs14" style={{ direction: "ltr" }}>
                ({meetingStartTime?.format(CUSTOM_TIME_FORMAT)} – {meetingEndTime?.format(CUSTOM_TIME_FORMAT)})
              </Text>
            </Flex>
          </>
        )}
      </div>
    )
  }, [data, taskLocation])

  return (
    <PopOver
      trigger="click"
      content={popoverContent}
      visible={isPopoverOpen && !isLoadingProjects && !isLoadingOffices}
      onVisibleChange={handlePopoverToggle}
      overlayInnerStyle={{
        padding: 10,
        minWidth: 260,
        maxWidth: 430,
        borderRadius: "8px",
        wordBreak: "break-word",
      }}
    >
      <Icon
        icon={currentStatus?.icon}
        color={currentStatus?.color}
        size={14}
        style={{ marginLeft: 6, cursor: "pointer" }}
        onClick={() => setIsPopoverOpen(true)}
        isLoading={isLoadingOffices || isLoadingProjects}
      />
    </PopOver>
  )
}

export default MeetingTaskStatus
